import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Save, Loader2, FileText, ExternalLink, ImageIcon } from "lucide-react";
import adminApi, { STATIC_ORIGIN } from "./adminApi";
// import { CKEditor } from "@ckeditor/ckeditor5-react";
// import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Font,
  Alignment,
  List,
  Link,
  BlockQuote,
  Table as CKTable,
  TableToolbar,
  Image as CKImage,
  ImageUpload,
  ImageToolbar,
  ImageStyle,
  ImageResize,
  SimpleUploadAdapter,
  HorizontalLine,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";

const IMAGE_PAGE_IDS = ["11", "14", "17", "19"];

const emptyForm = {
  page_id: "",
  cont_title: "",
  cont_details: "",
  ischecked: false,
};

function fileUrl(relativePath) {
  if (!relativePath) return null;
  return `${STATIC_ORIGIN}/${relativePath}`;
}

export default function ManageContents() {
  const [pages, setPages] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [existingImg, setExistingImg] = useState(null);
  const [existingAttachment, setExistingAttachment] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [showImageField, setShowImageField] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const [toast, setToast] = useState(null);
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchPages = async () => {
    try {
      const res = await adminApi.get("/admin/contents/meta/pages");
      const list = res.data.pages || [];
      setPages(list);
      if (list.length > 0) loadPage(String(list[0].parid));
    } catch (err) {
      console.error("Could not load page list:", err);
    }
  };

  const loadPage = async (pageId) => {
    if (!pageId) return;
    setLoading(true);
    setFormError("");
    setImageFile(null);
    setPdfFile(null);
    try {
      const res = await adminApi.get(`/admin/contents/by-page/${pageId}`);
      const d = res.data.item;
      setForm({
        page_id: pageId,
        cont_title: d.cont_title || "",
        cont_details: d.cont_details || "",
        ischecked: !!Number(d.ischecked),
      });
      setExistingImg(d.cont_img);
      setExistingAttachment(d.attachment);
      setShowImageField(res.data.showImageField);
    } catch (err) {
      setFormError(err.response?.data?.message || "Could not load this page's content.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  const handlePageChange = (e) => {
    const pageId = e.target.value;
    loadPage(pageId);
  };

  const handleImageChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
  };

  const handlePdfChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      setFormError("Only PDF files are allowed.");
      return;
    }
    setFormError("");
    setPdfFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.page_id) return setFormError("Page Name is required.");
    if (!form.cont_title.trim()) return setFormError("Page Title is required.");
    if (form.ischecked && !pdfFile && !existingAttachment) {
      return setFormError("A PDF file is required when 'Add PDF for this menu' is checked.");
    }

    const fd = new FormData();
    fd.append("page_id", form.page_id);
    fd.append("cont_title", form.cont_title.trim());
    fd.append("cont_details", form.cont_details);
    fd.append("ischecked", form.ischecked ? "1" : "0");
    if (imageFile) fd.append("txtImage", imageFile);
    if (pdfFile) fd.append("txtFileUpload", pdfFile);

    setSaving(true);
    try {
      await adminApi.post("/admin/contents", fd);
      showToast("success", "Content saved.");
      loadPage(form.page_id); // refresh to pick up new file paths
    } catch (err) {
      setFormError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="shrink-0 mb-3">
        <h3 className="font-display font-semibold text-lg text-[#0B2545]">Content Management</h3>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 overflow-auto p-6">
        <form onSubmit={handleSubmit} className="max-w-2xl space-y-5">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">
              Page Name <span className="text-red-500">*</span>
            </label>
            <select
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
              value={form.page_id}
              onChange={handlePageChange}
            >
              {pages.map((p) => (
                <option key={p.parid} value={p.parid}>{p.par_value}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm py-6">
              <Loader2 size={16} className="animate-spin" /> Loading content...
            </div>
          ) : (
            <>
              {showImageField && (
                <div>
                 <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Image Upload{" "}
                    <span className="text-red-500">
                      *<br />
                      (Medical Superintendent &amp; Dean and Principal image size 627 × 300)
                    </span>
                  </label>
                  <div
                    onClick={() => imageInputRef.current?.click()}
                    className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-[#0EA5A4] transition bg-white"
                  >
                    <div className="w-12 h-12 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                      <ImageIcon size={18} className="text-slate-300" />
                    </div>
                    <div className="text-xs text-slate-500">
                      {imageFile ? imageFile.name : existingImg ? "Click to replace banner image" : "Click to choose an image"}
                    </div>
                    <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
                  </div>
                  {existingImg && !imageFile && (
                    <a href={fileUrl(existingImg)} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#0EA5A4] hover:underline mt-1.5">
                      View current image <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              )}

              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                  Page Title <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  value={form.cont_title}
                  onChange={(e) => setForm({ ...form, cont_title: e.target.value })}
                  maxLength={100}
                />
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={form.ischecked}
                  onChange={(e) => setForm({ ...form, ischecked: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                />
                <span className="text-sm font-medium text-slate-700">Add PDF for this menu</span>
              </label>

              <AnimatePresence initial={false}>
                {form.ischecked && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }} className="overflow-hidden"
                  >
                    <div className="bg-slate-50 rounded-lg p-4">
                      <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                        File Upload (PDF only) <span className="text-red-500">*</span>
                      </label>
                      <div
                        onClick={() => pdfInputRef.current?.click()}
                        className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-[#0EA5A4] transition bg-white"
                      >
                        <div className="w-12 h-12 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                          <FileText size={18} className="text-slate-300" />
                        </div>
                        <div className="text-xs text-slate-500">
                          {pdfFile ? pdfFile.name : existingAttachment ? "Click to replace attachment" : "Click to choose a PDF file"}
                        </div>
                        <input ref={pdfInputRef} type="file" accept=".pdf,application/pdf" onChange={handlePdfChange} className="hidden" />
                      </div>
                      {existingAttachment && !pdfFile && (
                        <a href={fileUrl(existingAttachment)} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#0EA5A4] hover:underline mt-1.5">
                          View current PDF <ExternalLink size={11} />
                        </a>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div>
                <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                  Content Details <span className="text-red-500">*</span>
                </label>
                {/* Swap-in point for CKEditor / TinyMCE / react-quill if you want rich text */}
                {/* <textarea
                  rows={10}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition resize-y"
                  value={form.cont_details}
                  onChange={(e) => setForm({ ...form, cont_details: e.target.value })}
                /> */}
                   {/* <CKEditor
    editor={ClassicEditor}
    data={form.cont_details}
    onChange={(event, editor) => {
      const data = editor.getData();

      setForm((prev) => ({
        ...prev,
        cont_details: data,
      }));
    }}
  /> */}

   <CKEditor
      editor={ClassicEditor}
      data={form.cont_details}
      config={{
        licenseKey: "GPL",
        plugins: [
          Essentials, Paragraph, Heading,
          Bold, Italic, Underline,
          Font, Alignment,
          List, Link, BlockQuote,
          CKTable, TableToolbar,
          CKImage, ImageUpload, ImageToolbar, ImageStyle, ImageResize,
          SimpleUploadAdapter,
          HorizontalLine,
        ],
        toolbar: [
          "heading", "|",
          "fontFamily", "fontSize", "|",
          "bold", "italic", "underline", "|",
          "fontColor", "fontBackgroundColor", "|",
          "alignment", "|",
          "bulletedList", "numberedList", "|",
          "link", "blockQuote", "insertTable", "uploadImage", "horizontalLine", "|",
          "undo", "redo",
        ],
        fontFamily: {
          options: [
            "default",
            "Arial, Helvetica, sans-serif",
            "Georgia, serif",
            "Times New Roman, Times, serif",
            "Courier New, Courier, monospace",
            "Verdana, Geneva, sans-serif",
            "Tahoma, Geneva, sans-serif",
            "Trebuchet MS, Helvetica, sans-serif",
          ],
          supportAllValues: true,
        },
        fontSize: {
          options: [9, 11, 13, "default", 17, 19, 21, 24, 28, 32],
          supportAllValues: true,
        },
        table: {
          contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
        },
        image: {
          toolbar: [
            "imageStyle:alignLeft",
            "imageStyle:alignRight",
            "imageStyle:block",
            "|",
            "toggleImageCaption",
            "imageTextAlternative",
            "resizeImage",
          ],
        },
        simpleUpload: {
          uploadUrl: `${adminApi.defaults.baseURL}/admin/contents/upload-image`,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("mkcg_admin_token")}`,
          },
        },
      }}
      onChange={(event, editor) => {
        const data = editor.getData();
        setForm((prev) => ({
          ...prev,
          cont_details: data,
        }));
      }}
    />



              </div>

              {formError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{formError}</p>
              )}

              <button
                type="submit" disabled={saving}
                className="flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg px-6 py-2.5 transition disabled:opacity-60"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={15} />}
                {saving ? "Saving..." : "Submit"}
              </button>
            </>
          )}
        </form>
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}>
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}